import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
//import 'bootstrap/dist/css/bootstrap.min.css';

const API_URL = process.env.REACT_APP_API_URL;
const GET_PERIODS_URL = `${API_URL}/Custom/GetExamPeriods`;
const GET_FACULTIES_URL = `${API_URL}/Custom/GetFaculties`;
const GET_TIMESLOTS_URL = `${API_URL}/Custom/GetTimeSlots`;
const GET_EXAMS_URL = `${API_URL}/Custom/GetExams`;
const GET_BUILDINGS_URL = `${API_URL}/Custom/GetBuildings`;
const GET_ROOMS_URL = `${API_URL}/Custom/GetRooms`;
const POST_SCHEDULE_URL = `${API_URL}/Custom/ScheduleExams`;
const VALIDATE_GROUP_URL = `${API_URL}/Custom/ValidateExamGroup`;
const GET_DRAFTS_URL = `${API_URL}/Custom/GetExamSchedulerDrafts`;
const SAVE_DRAFT_URL = `${API_URL}/Custom/SaveExamSchedulerDraft`;
const DELETE_DRAFT_URL = `${API_URL}/Custom/DeleteExamSchedulerDraft`;
const RESET_SCHEDULE_URL = `${API_URL}/Custom/ResetExamSchedule`;
const LIST_ORGANIZATIONS_URL = `${API_URL}/Custom/ListExamOrganizations`;
const CANCEL_ORGANIZATION_URL = `${API_URL}/Custom/CancelExamOrganization`;
const SPLIT_ORGANIZATION_URL = `${API_URL}/Custom/SplitExamOrganization`;
const GET_EXAM_REFERENCE_GROUP_URL = `${API_URL}/Custom/GetExamReferenceGroup`;

const DEFAULT_MAX_DAILY_EXAM_COUNT = 3;
const DEFAULT_MAX_DAILY_OWN_DEPARTMENT_EXAM_COUNT = 2;

const formatScheduleError = data => {
    if (!data) return 'Bilinmeyen hata.';
    if (typeof data === 'string') return data;

    const lines = [];
    if (data.message) lines.push(data.message);

    if (data.summary) {
        const { selectedSlotCount, selectedClassroomCount, distinctStudentCount, studentRecordCount, selectedClassroomTotalCapacity } = data.summary;
        lines.push(`Özet: ${selectedSlotCount ?? 0} slot, ${selectedClassroomCount ?? 0} sınıf, ${distinctStudentCount ?? 0} öğrenci (${studentRecordCount ?? 0} kayıt), seçili toplam kapasite ${selectedClassroomTotalCapacity ?? 0}.`);
    }

    if (Array.isArray(data.checkedRules) && data.checkedRules.length > 0) {
        lines.push(`Kontroller: ${data.checkedRules.join(' | ')}`);
    }

    if (Array.isArray(data.slotReports) && data.slotReports.length > 0) {
        data.slotReports.forEach((slot, index) => {
            lines.push(`Slot ${index + 1}: ${slot.slot || slot.Slot || '-'}`);
            const reasons = slot.reasons || slot.Reasons || [];
            const checks = slot.checks || slot.Checks || [];
            const samples = slot.samples || slot.Samples || [];
            const freeRooms = slot.freeRooms || slot.FreeRooms || [];

            if (reasons.length > 0) lines.push(`  Neden: ${reasons.join(' | ')}`);
            if (checks.length > 0) lines.push(`  Yapılan kontroller: ${checks.join(' | ')}`);
            if (samples.length > 0) lines.push(`  Örnekler: ${samples.join(' | ')}`);
            if (freeRooms.length > 0) lines.push(`  Boş sınıflar: ${freeRooms.join(', ')}`);
        });
    }

    if (lines.length > 0) return lines.join('\n');

    try {
        return JSON.stringify(data);
    } catch {
        return 'Planlama sırasında hata oluştu.';
    }
};

export default function ExamSchedulerComponent() {
    const [step, setStep] = useState(1);

    const [examPeriods, setExamPeriods] = useState([]);
    const [selectedExamPeriod, setSelectedExamPeriod] = useState(null);
    const [periodReloadKey, setPeriodReloadKey] = useState(0);

    const [drafts, setDrafts] = useState([]);
    const [pendingDraft, setPendingDraft] = useState(null);

    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);

    const [faculties, setFaculties] = useState([]);
    const [selectedFaculty, setSelectedFaculty] = useState(null);

    const [exams, setExams] = useState([]);
    const [allExams, setAllExams] = useState([]);
    const [selectedExams, setSelectedExams] = useState([]);

    // Manuel gruplar için
    const [examGroups, setExamGroups] = useState([]);
    const [selectedForGroup, setSelectedForGroup] = useState([]);

    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);

    const [rooms, setRooms] = useState([]);
    const [allRooms, setAllRooms] = useState([]);
    const [selectedRooms, setSelectedRooms] = useState([]);

    const [status, setStatus] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [maxDailyExamCount, setMaxDailyExamCount] = useState(DEFAULT_MAX_DAILY_EXAM_COUNT);
    const [maxDailyOwnDepartmentExamCount, setMaxDailyOwnDepartmentExamCount] = useState(DEFAULT_MAX_DAILY_OWN_DEPARTMENT_EXAM_COUNT);

    const [organizations, setOrganizations] = useState([]);
    const [organizationsLoading, setOrganizationsLoading] = useState(false);
    const [organizationError, setOrganizationError] = useState('');
    const [cancelingKey, setCancelingKey] = useState(null);
    const [splitTarget, setSplitTarget] = useState(null);
    const [splitGroupCount, setSplitGroupCount] = useState(2);
    const [splitRows, setSplitRows] = useState([]);
    const [splitBuildings, setSplitBuildings] = useState([]);
    const [splitRoomsByBuilding, setSplitRoomsByBuilding] = useState({});
    const [splittingKey, setSplittingKey] = useState(null);

    // ——— Manuel grupların birleşik listesi ———
    const allGroups = useMemo(() => [
        ...examGroups
    ], [examGroups]);

    // ——— Henüz hiçbir gruba katılmamış sınavlar ———
    const ungroupedExams = useMemo(() => {
        const grouped = new Set(allGroups.flat());
        return selectedExams.filter(id => !grouped.has(id));
    }, [selectedExams, allGroups]);

    // ——— Manuel grup işlemleri ———
    /*const createGroup = () => {
        if (selectedForGroup.length < 2) return;
        setExamGroups(prev => [...prev, selectedForGroup]);
        setSelectedForGroup([]);
    };
    */

    const selectedStudentTotal = useMemo(() => {
        return selectedForGroup.reduce((sum, id) => {
            const exam = allExams.find(e => e.id === id);
            return sum + (exam?.studentCount || 0);
        }, 0);
    }, [selectedForGroup, allExams]);

    const normalizePositiveInteger = (value, fallback) => {
        const number = Number(value);
        if (!Number.isFinite(number) || number <= 0) return fallback;
        return Math.floor(number);
    };

    const handleMaxDailyExamCountChange = value => {
        const nextValue = normalizePositiveInteger(value, DEFAULT_MAX_DAILY_EXAM_COUNT);
        setMaxDailyExamCount(nextValue);
        setMaxDailyOwnDepartmentExamCount(prev => Math.min(prev, nextValue));
    };

    const handleMaxDailyOwnDepartmentExamCountChange = value => {
        const nextValue = normalizePositiveInteger(value, DEFAULT_MAX_DAILY_OWN_DEPARTMENT_EXAM_COUNT);
        setMaxDailyOwnDepartmentExamCount(Math.min(nextValue, maxDailyExamCount));
    };

    const createGroup = async () => {
        if (selectedForGroup.length < 2) return;

        try {
            const { data } = await axios.post(VALIDATE_GROUP_URL, selectedForGroup);
            if (data.valid) {
                setExamGroups(prev => [...prev, selectedForGroup]);
                setSelectedForGroup([]);
            } else {
                alert(
                    `Bu sınavlar aynı öğrenciyi içeriyor. Çakışan öğrenci sayısı: ${data.conflictCount}`
                );
            }
        } catch (err) {
            console.error(err);
            alert('Grup doğrulama sırasında hata oluştu.');
        }
    };

    const removeGroup = idx =>
        setExamGroups(prev => prev.filter((_, i) => i !== idx));

    const formatDraftDate = draft => {
        if (!draft) return '';
        const iso = draft.lastUsedAt || draft.updatedAt || draft.createdAt;
        if (!iso) return '';
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('tr-TR');
    };

    const formatSlotLabel = slot => {
        if (!slot) return '';
        const date = slot.exactDate
            ? new Date(slot.exactDate).toLocaleDateString('tr-TR', {
                weekday: 'short',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })
            : 'Tarihsiz';
        const start = slot.startTime ? slot.startTime.substring(0, 5) : '';
        const end = slot.endTime ? slot.endTime.substring(0, 5) : '';
        return `${date} ${start} - ${end}`;
    };

    const distributeSplitCounts = (total, count) => {
        const safeCount = Math.max(2, count || 2);
        const base = Math.floor((total || 0) / safeCount);
        const remainder = (total || 0) % safeCount;
        return Array.from({ length: safeCount }, (_, index) => base + (index < remainder ? 1 : 0));
    };

    const buildDefaultSplitRows = (item, count) => {
        const sortedSlots = [...timeSlots].sort((a, b) => {
            const dateCompare = String(a.exactDate || '').localeCompare(String(b.exactDate || ''));
            if (dateCompare !== 0) return dateCompare;
            return String(a.startTime || '').localeCompare(String(b.startTime || ''));
        });
        const sourceIndex = sortedSlots.findIndex(slot => slot.id === item.timeSlotId);
        const counts = distributeSplitCounts(item.seatAssignmentCount || 0, count);

        return counts.map((studentCount, index) => {
            const slot = sortedSlots[sourceIndex >= 0 ? sourceIndex + index : index];
            return {
                timeSlotId: slot?.id || '',
                buildingId: item.buildingId || '',
                classroomId: item.classroomId || '',
                studentCount
            };
        });
    };

    const ensureSplitRoomsForBuilding = async buildingId => {
        if (!buildingId || splitRoomsByBuilding[buildingId]) return;
        const { data } = await axios.get(GET_ROOMS_URL, { params: { buildingId } });
        setSplitRoomsByBuilding(prev => ({
            ...prev,
            [buildingId]: data || []
        }));
    };

    const openSplitModal = async item => {
        const count = 2;
        setSplitTarget(item);
        setSplitGroupCount(count);
        setSplitRows(buildDefaultSplitRows(item, count));

        try {
            if (splitBuildings.length === 0) {
                const { data } = await axios.get(GET_BUILDINGS_URL);
                setSplitBuildings(data || []);
            }
            if (item.buildingId) {
                await ensureSplitRoomsForBuilding(item.buildingId);
            }
        } catch (err) {
            console.error('Bölme modalı için bina/sınıf listesi alınamadı', err);
            alert('Bina ve sınıf listesi alınamadı.');
        }
    };

    const closeSplitModal = () => {
        setSplitTarget(null);
        setSplitRows([]);
        setSplitGroupCount(2);
    };

    const handleSplitCountChange = value => {
        const nextCount = Math.max(2, Math.min(12, Number(value) || 2));
        setSplitGroupCount(nextCount);
        if (splitTarget) {
            setSplitRows(buildDefaultSplitRows(splitTarget, nextCount));
        }
    };

    const updateSplitRow = (index, patch) => {
        setSplitRows(prev => prev.map((row, rowIndex) => (
            rowIndex === index ? { ...row, ...patch } : row
        )));
    };

    const handleSplitSubmit = async () => {
        if (!splitTarget) return;

        const expectedTotal = splitTarget.seatAssignmentCount || 0;
        const actualTotal = splitRows.reduce((sum, row) => sum + (Number(row.studentCount) || 0), 0);

        if (splitRows.some(row => !row.timeSlotId || !row.classroomId || !row.studentCount || Number(row.studentCount) <= 0)) {
            alert('Her grup için slot, sınıf ve öğrenci sayısı girilmelidir.');
            return;
        }

        if (actualTotal !== expectedTotal) {
            alert(`Grup toplamı ${expectedTotal} öğrenci olmalıdır. Girilen toplam: ${actualTotal}`);
            return;
        }

        const targetKeys = splitRows.map(row => `${row.timeSlotId}_${row.classroomId}`);
        if (new Set(targetKeys).size !== targetKeys.length) {
            alert('Aynı slot ve sınıf birden fazla grup için seçilemez.');
            return;
        }

        const confirmed = typeof window !== 'undefined'
            ? window.confirm('Bu işlem mevcut oturma atamalarını yeniden oluşturacak ve varsa görevlileri yeni gruplara kopyalayacak. Devam etmek istiyor musunuz?')
            : true;

        if (!confirmed) return;

        const key = `${splitTarget.timeSlotId}_${splitTarget.classroomId}`;
        setSplittingKey(key);
        setStatus('Sınav gruplara bölünüyor...');

        try {
            const { data } = await axios.post(SPLIT_ORGANIZATION_URL, {
                ExamPeriodId: selectedExamPeriod,
                SourceTimeSlotId: splitTarget.timeSlotId,
                SourceClassroomId: splitTarget.classroomId,
                Groups: splitRows.map(row => ({
                    TimeSlotId: Number(row.timeSlotId),
                    ClassroomId: Number(row.classroomId),
                    StudentCount: Number(row.studentCount)
                }))
            });

            setStatus(data?.message || 'Sınav gruplara bölündü.');
            closeSplitModal();
            setPeriodReloadKey(prev => prev + 1);
        } catch (err) {
            console.error('Sınav bölme işlemi başarısız', err);
            const errorMessage = err?.response?.data?.message || err?.response?.data || 'Sınav bölme işlemi sırasında hata oluştu.';
            setStatus(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
            alert(typeof errorMessage === 'string' ? errorMessage : 'Sınav bölme işlemi sırasında hata oluştu.');
        } finally {
            setSplittingKey(null);
        }
    };

    useEffect(() => {
        let mounted = true;
        axios.get(GET_DRAFTS_URL)
            .then(res => {
                if (!mounted) return;
                const data = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.data)
                        ? res.data.data
                        : [];
                setDrafts(data);
            })
            .catch(err => {
                console.error('Exam scheduler draft yükleme hatası', err);
            });

        return () => {
            mounted = false;
        };
    }, []);

    // Scroll to top
    useEffect(() => window.scrollTo(0, 0), [step]);

    // Load periods
    useEffect(() => {
        axios.get(GET_PERIODS_URL)
            .then(res => setExamPeriods(res.data))
            .catch(console.error);
    }, []);

    // On period change
    useEffect(() => {
        if (!selectedExamPeriod) return;
        axios.get(`${GET_TIMESLOTS_URL}?examPeriodId=${selectedExamPeriod}`)
            .then(res => setTimeSlots(res.data || []))
            .catch(console.error);
        axios.get(GET_FACULTIES_URL)
            .then(res => setFaculties(
                (res.data || [])
                    .map(o => ({ id: o.id, name: o.name }))
                    .sort((a, b) => a.name.localeCompare(b.name))
            ))
            .catch(console.error);

        // Reset downstream
        setSelectedFaculty(null);
        setExams([]); setAllExams([]);
        setSelectedTimeSlots([]);
        setBuildings([]); setSelectedBuilding(null);
        setRooms([]); setAllRooms([]);
        setSelectedExams([]); setSelectedRooms([]);
        setExamGroups([]); setSelectedForGroup([]);
    }, [selectedExamPeriod, periodReloadKey]);

    useEffect(() => {
        if (!selectedExamPeriod) {
            setOrganizations([]);
            setOrganizationError('');
            return;
        }

        let cancelled = false;
        setOrganizationsLoading(true);
        setOrganizationError('');

        axios.get(`${LIST_ORGANIZATIONS_URL}?examPeriodId=${selectedExamPeriod}`)
            .then(res => {
                if (cancelled) return;
                const data = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.data)
                        ? res.data.data
                        : [];
                setOrganizations(data);
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Planlanmış sınav organizasyonları alınamadı', err);
                const message = err?.response?.data?.message || 'Planlanmış salonlar yüklenemedi.';
                setOrganizationError(message);
            })
            .finally(() => {
                if (cancelled) return;
                setOrganizationsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [selectedExamPeriod, periodReloadKey]);

    useEffect(() => {
        setStatus('');
    }, [selectedExamPeriod]);

    useEffect(() => {
        if (!pendingDraft) return;
        if (!selectedExamPeriod || pendingDraft.selectedExamPeriod !== selectedExamPeriod) return;

        const savedSlots = Array.isArray(pendingDraft.selectedTimeSlots) ? pendingDraft.selectedTimeSlots : [];
        const shouldWaitSlots = savedSlots.length > 0 && (!Array.isArray(timeSlots) || timeSlots.length === 0);
        if (shouldWaitSlots) {
            return;
        }

        const slotIds = savedSlots.length === 0
            ? []
            : savedSlots.filter(id => timeSlots.some(ts => ts.id === id));
        setSelectedTimeSlots(slotIds);

        const draftMaxDailyExamCount = normalizePositiveInteger(pendingDraft.maxDailyExamCount, DEFAULT_MAX_DAILY_EXAM_COUNT);
        const draftMaxDailyOwnDepartmentExamCount = Math.min(
            normalizePositiveInteger(pendingDraft.maxDailyOwnDepartmentExamCount, DEFAULT_MAX_DAILY_OWN_DEPARTMENT_EXAM_COUNT),
            draftMaxDailyExamCount
        );
        setMaxDailyExamCount(draftMaxDailyExamCount);
        setMaxDailyOwnDepartmentExamCount(draftMaxDailyOwnDepartmentExamCount);

        const savedExams = Array.isArray(pendingDraft.selectedExams) ? pendingDraft.selectedExams : [];
        setSelectedExams(savedExams);

        const savedExamGroups = Array.isArray(pendingDraft.examGroups)
            ? pendingDraft.examGroups.map(group => Array.isArray(group) ? [...group] : [])
            : [];
        setExamGroups(savedExamGroups);
        const examDetails = Array.isArray(pendingDraft.examDetails) ? pendingDraft.examDetails : [];
        if (examDetails.length > 0) {
            setAllExams(prev => {
                const map = new Map(prev.map(ex => [ex.id, ex]));
                examDetails.forEach(ex => {
                    if (ex && ex.id != null) {
                        map.set(ex.id, ex);
                    }
                });
                return Array.from(map.values());
            });
        }

        const roomIds = Array.isArray(pendingDraft.selectedRooms) ? pendingDraft.selectedRooms : [];
        setSelectedRooms(roomIds);

        const roomDetails = Array.isArray(pendingDraft.roomDetails) ? pendingDraft.roomDetails : [];
        if (roomDetails.length > 0) {
            setAllRooms(prev => {
                const map = new Map(prev.map(r => [r.id, r]));
                roomDetails.forEach(room => {
                    if (room && room.id != null) {
                        map.set(room.id, room);
                    }
                });
                return Array.from(map.values());
            });
        }

        setPendingDraft(null);
        setStep(5);
        setStatus('Kaydedilen taslak yüklendi.');
    }, [pendingDraft, selectedExamPeriod, timeSlots]);

    // On faculty change
    useEffect(() => {
        if (!selectedExamPeriod || !selectedFaculty) {
            setExams([]); return;
        }
        axios.get(GET_EXAMS_URL, { params: { examPeriodId: selectedExamPeriod, facultyId: selectedFaculty } })
            .then(res => {
                const data = (res.data || []).sort((a, b) => a.name.localeCompare(b.name));
                setExams(data);
                setAllExams(prev => {
                    const map = new Map(prev.map(e => [e.id, e]));
                    data.forEach(ex => map.set(ex.id, ex));
                    return Array.from(map.values());
                });
            })
            .catch(console.error);
    }, [selectedFaculty, selectedExamPeriod]);

    // On step 4
    useEffect(() => {
        if (step !== 4) return;
        axios.get(GET_BUILDINGS_URL)
            .then(res => setBuildings(
                (res.data || [])
                    .map(o => ({ id: o.id, name: o.name }))
                    .sort((a, b) => a.name.localeCompare(b.name))
            ))
            .catch(console.error);
    }, [step]);

    // On building change
    useEffect(() => {
        if (!selectedBuilding) return setRooms([]);
        axios.get(GET_ROOMS_URL, { params: { buildingId: selectedBuilding } })
            .then(res => {
                const list = (res.data || []).map(o => ({
                    id: o.id,
                    name: o.name,
                    buildingId: selectedBuilding,
                    buildingName: buildings.find(b => b.id === selectedBuilding)?.name
                }));
                list.sort((a, b) => a.name.localeCompare(b.name));
                setRooms(list);
                setAllRooms(prev => {
                    const map = new Map(prev.map(r => [r.id, r]));
                    list.forEach(r => map.set(r.id, r));
                    return Array.from(map.values());
                });
            })
            .catch(console.error);
    }, [selectedBuilding, buildings]);

    // Helpers
    const next = () => setStep(s => Math.min(s + 1, 5));
    const prev = () => setStep(s => Math.max(s - 1, 1));
    const findPeriodName = id => examPeriods.find(ep => ep.id === id)?.name || '';
    const formatSlots = () => {
        const byDate = {};
        selectedTimeSlots.forEach(id => {
            const ts = timeSlots.find(x => x.id === id);
            if (!ts) return;
            const date = new Date(ts.exactDate)
                .toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'numeric' });
            const time = `${ts.startTime.substring(0, 5)} - ${ts.endTime.substring(0, 5)}`;
            byDate[date] = byDate[date] || [];
            byDate[date].push(time);
        });
        return byDate;
    };

    const selectedExamObjs = allExams.filter(ex => selectedExams.includes(ex.id));
    const examsByFaculty = selectedExamObjs.reduce((acc, ex) => {
        acc[ex.facultyName] = acc[ex.facultyName] || [];
        acc[ex.facultyName].push(ex);
        return acc;
    }, {});

    const selectedRoomObjs = allRooms.filter(r => selectedRooms.includes(r.id));
    const roomsByBuilding = selectedRoomObjs.reduce((acc, r) => {
        acc[r.buildingName] = acc[r.buildingName] || [];
        acc[r.buildingName].push(r);
        return acc;
    }, {});

    const mergeExamDetails = items => {
        if (!Array.isArray(items)) return;

        setAllExams(prev => {
            const map = new Map(prev.map(ex => [ex.id, ex]));

            items.forEach(item => {
                if (!item || item.examId == null) return;

                map.set(item.examId, {
                    id: item.examId,
                    name: item.name,
                    facultyId: item.facultyId,
                    facultyName: item.facultyName,
                    studentCount: item.studentCount,
                    alreadyScheduled: item.alreadyScheduled,
                });
            });

            return Array.from(map.values());
        });
    };

    const calculateGroupStudentCount = group =>
        group.reduce((sum, id) => {
            const exam = allExams.find(e => e.id === id);
            return sum + (exam?.studentCount || 0);
        }, 0);

    const toggleExamSelection = async ex => {
        const alreadySelected = selectedExams.includes(ex.id);

        if (alreadySelected) {
            setSelectedExams(prev => prev.filter(x => x !== ex.id));
            return;
        }

        setSelectedExams(prev => [...prev, ex.id]);

        if (!selectedExamPeriod) {
            return;
        }

        try {
            Swal.fire({
                title: 'Referans sınavlar getiriliyor... ',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            const { data } = await axios.get(GET_EXAM_REFERENCE_GROUP_URL, {
                params: { examId: ex.id, examPeriodId: selectedExamPeriod }
            });

            const response = data || {};
            const groupItems = Array.isArray(response.exams) ? response.exams : [];

            mergeExamDetails(groupItems);

            const selectableIds = groupItems
                .filter(item => item && !item.alreadyScheduled)
                .map(item => item.examId);

            if (response.isMainExam && selectableIds.length > 1) {
                setSelectedExams(prev => Array.from(new Set([...prev, ...selectableIds])));

                setExamGroups(prev => {
                    const exists = prev.some(group =>
                        group.length === selectableIds.length &&
                        group.every(id => selectableIds.includes(id))
                    );

                    return exists ? prev : [...prev, selectableIds];
                });
            }
        } catch (err) {
            console.error('Ana sınav grubu alınamadı', err);
        } finally {
            Swal.close();
        }
    };

    const handleSaveDraft = async () => {
        if (!selectedExamPeriod) {
            alert('Taslak oluşturmak için bir sınav dönemi seçmelisiniz.');
            return;
        }

        const labelDefault = `${findPeriodName(selectedExamPeriod) || 'Taslak'} - ${new Date().toLocaleString('tr-TR')}`;
        const label = typeof window !== 'undefined'
            ? window.prompt('Taslak adını girin', labelDefault)
            : labelDefault;

        if (!label) {
            return;
        }

        const payload = {
            label,
            data: {
                selectedExamPeriod,
                maxDailyExamCount,
                maxDailyOwnDepartmentExamCount,
                selectedTimeSlots: [...selectedTimeSlots],
                selectedExams: [...selectedExams],
                examGroups: examGroups.map(group => Array.isArray(group) ? [...group] : []),
                selectedRooms: [...selectedRooms],
                examDetails: selectedExamObjs.map(ex => ({
                    id: ex.id,
                    name: ex.name,
                    facultyName: ex.facultyName,
                    studentCount: ex.studentCount,
                    alreadyScheduled: ex.alreadyScheduled,
                })),
                roomDetails: selectedRoomObjs.map(r => ({
                    id: r.id,
                    name: r.name,
                    buildingId: r.buildingId,
                    buildingName: r.buildingName,
                })),
            }
        };

        try {
            setStatus('Taslak kaydediliyor...');
            const { data } = await axios.post(SAVE_DRAFT_URL, payload);
            if (data && typeof data === 'object') {
                setDrafts(prev => {
                    const current = Array.isArray(prev) ? prev : [];
                    const filtered = current.filter(d => d.id !== data.id);
                    return [data, ...filtered];
                });
            }
            setStatus('Taslak kaydedildi.');
        } catch (err) {
            console.error('Taslak kaydedilemedi', err);
            setStatus('Taslak kaydedilirken hata oluştu.');
            alert('Taslak kaydedilirken bir hata oluştu.');
        }
    };

    const handleLoadDraft = draft => {
        if (!draft) return;
        const periodNumber = Number(draft.selectedExamPeriod);
        const sanitizedMaxDailyExamCount = normalizePositiveInteger(draft.maxDailyExamCount, DEFAULT_MAX_DAILY_EXAM_COUNT);
        const sanitizedMaxDailyOwnDepartmentExamCount = Math.min(
            normalizePositiveInteger(draft.maxDailyOwnDepartmentExamCount, DEFAULT_MAX_DAILY_OWN_DEPARTMENT_EXAM_COUNT),
            sanitizedMaxDailyExamCount
        );
        const sanitized = {
            ...draft,
            selectedExamPeriod: Number.isNaN(periodNumber) ? (draft.selectedExamPeriod ?? null) : periodNumber,
            maxDailyExamCount: sanitizedMaxDailyExamCount,
            maxDailyOwnDepartmentExamCount: sanitizedMaxDailyOwnDepartmentExamCount,
            selectedTimeSlots: Array.isArray(draft.selectedTimeSlots)
                ? draft.selectedTimeSlots
                    .map(Number)
                    .filter(id => !Number.isNaN(id))
                : [],
            selectedExams: Array.isArray(draft.selectedExams)
                ? draft.selectedExams
                    .map(Number)
                    .filter(id => !Number.isNaN(id))
                : [],
            examGroups: Array.isArray(draft.examGroups)
                ? draft.examGroups.map(group => Array.isArray(group)
                    ? group
                        .map(Number)
                        .filter(id => !Number.isNaN(id))
                    : [])
                : [],
            selectedRooms: Array.isArray(draft.selectedRooms)
                ? draft.selectedRooms
                    .map(Number)
                    .filter(id => !Number.isNaN(id))
                : [],
            examDetails: Array.isArray(draft.examDetails)
                ? draft.examDetails.map(detail => detail || {})
                : [],
            roomDetails: Array.isArray(draft.roomDetails)
                ? draft.roomDetails.map(detail => detail || {})
                : [],
        };

        setPendingDraft(sanitized);
        setSelectedExamPeriod(sanitized.selectedExamPeriod);
        setStep(1);
    };

    const handleResetSchedule = async () => {
        if (!selectedExamPeriod || isResetting) {
            if (!selectedExamPeriod) {
                alert('Önce sınav dönemi seçmelisiniz.');
            }
            return;
        }

        const confirmed = typeof window !== 'undefined'
            ? window.confirm('Bu dönem için mevcut planlamayı sıfırlamak istediğinize emin misiniz?')
            : true;

        if (!confirmed) {
            return;
        }

        setIsResetting(true);

        try {
            setStatus('Planlama sıfırlanıyor...');
            const { data } = await axios.post(RESET_SCHEDULE_URL, { examPeriodId: selectedExamPeriod });
            const message = data?.message || 'Planlama sıfırlandı.';
            setStatus(message);
            setPeriodReloadKey(key => key + 1);
            setStep(1);
        } catch (err) {
            console.error('Planlama sıfırlanamadı', err);
            const errorMessage = err?.response?.data?.message
                || 'Planlama sıfırlanırken bir hata oluştu.';
            setStatus(errorMessage);
            alert(errorMessage);
        } finally {
            setIsResetting(false);
        }
    };

    const handleCancelOrganization = async item => {
        if (!selectedExamPeriod) {
            alert('Önce sınav dönemi seçmelisiniz.');
            return;
        }

        const confirmationText = 'Bu salon ve zaman dilimi için planlanan tüm sınavları ve görevleri iptal etmek istediğinize emin misiniz?';
        const confirmed = typeof window !== 'undefined'
            ? window.confirm(confirmationText)
            : true;

        if (!confirmed) {
            return;
        }

        const key = `${item.timeSlotId}_${item.classroomId}`;
        setCancelingKey(key);

        try {
            setStatus('Organizasyon iptal ediliyor...');
            const { data } = await axios.post(CANCEL_ORGANIZATION_URL, {
                examPeriodId: selectedExamPeriod,
                timeSlotId: item.timeSlotId,
                classroomId: item.classroomId,
            });
            const message = data?.message || 'Sınav organizasyonu iptal edildi.';
            setStatus(message);
            setOrganizations(prev => prev.filter(org =>
                org.timeSlotId !== item.timeSlotId || org.classroomId !== item.classroomId
            ));
        } catch (err) {
            console.error('Organizasyon iptal edilirken hata oluştu', err);
            const errorMessage = err?.response?.data?.message || 'Organizasyon iptal edilirken bir hata oluştu.';
            setStatus(errorMessage);
            if (typeof window !== 'undefined') {
                alert(errorMessage);
            }
        } finally {
            setCancelingKey(null);
        }
    };

    const handleDeleteDraft = async id => {
        if (!id) return;
        try {
            await axios.delete(`${DELETE_DRAFT_URL}/${id}`);
            setDrafts(prev => prev.filter(d => d.id !== id));
        } catch (err) {
            console.error('Taslak silinemedi', err);
            alert('Taslak silinirken bir hata oluştu.');
        }
    };

    // Submission
    const handleSubmit = async () => {
        setStatus('Planlanıyor...');
        try {
            const payloadGroups = [
                ...examGroups,
                ...ungroupedExams.map(id => [id])
            ];
            await axios.post(POST_SCHEDULE_URL, {
                ExamPeriodId: selectedExamPeriod,
                TimeSlotIds: selectedTimeSlots,
                ClassroomIds: selectedRooms,
                ExamGroups: payloadGroups,
                MaxDailyExamCount: maxDailyExamCount,
                MaxDailyOwnDepartmentExamCount: maxDailyOwnDepartmentExamCount
            });
            setStatus('Sınavlar başarıyla planlandı!');
        } catch (err) {
            setStatus('Hata:\n' + formatScheduleError(err.response?.data || err.message));
        }
    };

    const splitExpectedTotal = splitTarget?.seatAssignmentCount || 0;
    const splitActualTotal = splitRows.reduce((sum, row) => sum + (Number(row.studentCount) || 0), 0);
    const splitSourceSlot = splitTarget ? timeSlots.find(slot => slot.id === splitTarget.timeSlotId) : null;

    return (
        <div className="container mt-4">
            <h2 className="mb-4">Sınav Planlayıcı</h2>
            <ul className="nav nav-pills mb-4">
                {['Dönem', 'Slot', 'Fakülte & Ders', 'Sınıf', 'Onay'].map((lbl, i) => (
                    <li className="nav-item" key={i}>
                        <span className={`nav-link ${step === i + 1 ? 'active' : ''}`}>
                            {i + 1}. {lbl}
                        </span>
                    </li>
                ))}
            </ul>

            <div className="card p-4">

                {/* NAV */}
                <div className="mb-4 d-flex justify-content-between">
                    <button className="btn btn-secondary" onClick={prev} disabled={step === 1}>Geri</button>
                    <button className="btn btn-secondary" onClick={next} disabled={step === 5}>
                        {step < 5 ? 'İleri' : ''}
                    </button>
                </div>
                {/* STEP 1 */}
                {step === 1 && (
                    <>
                        <h5>1. Dönem Seç</h5>
                        <select
                            className="form-control"
                            value={selectedExamPeriod || ''}
                            onChange={e => setSelectedExamPeriod(Number(e.target.value))}
                        >
                            <option value="" disabled>Seçiniz</option>
                            {examPeriods.map(ep => (
                                <option key={ep.id} value={ep.id}>{ep.name}</option>
                            ))}
                        </select>
                        <div className="row g-3 mt-3">
                            <div className="col-md-6">
                                <label className="form-label">Aynı gün maksimum sınav</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    value={maxDailyExamCount}
                                    onChange={e => handleMaxDailyExamCountChange(e.target.value)}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Kendi bölümünden maksimum sınav</label>
                                <input
                                    type="number"
                                    min="1"
                                    max={maxDailyExamCount}
                                    className="form-control"
                                    value={maxDailyOwnDepartmentExamCount}
                                    onChange={e => handleMaxDailyOwnDepartmentExamCountChange(e.target.value)}
                                />
                            </div>
                        </div>
                        {selectedExamPeriod && (
                            <div className="mt-3">
                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={handleResetSchedule}
                                    disabled={isResetting}
                                >
                                    {isResetting ? 'Sıfırlanıyor...' : 'Planlamayı Sıfırla'}
                                </button>
                            </div>
                        )}
                        {selectedExamPeriod && (
                            <div className="mt-4">
                                <h6>Planlanmış Salonlar</h6>
                                {organizationsLoading && (
                                    <div className="text-muted">Yükleniyor...</div>
                                )}
                                {organizationError && (
                                    <div className="alert alert-danger" role="alert">{organizationError}</div>
                                )}
                                {!organizationsLoading && !organizationError && organizations.length === 0 && (
                                    <div className="alert alert-light border" role="status">
                                        Bu dönem için planlanmış salon bulunmuyor.
                                    </div>
                                )}
                                {!organizationsLoading && !organizationError && organizations.map(item => {
                                    const key = `${item.timeSlotId}_${item.classroomId}`;
                                    const slotDate = item.date ? new Date(item.date) : null;
                                    const formattedDate = slotDate
                                        ? slotDate.toLocaleDateString('tr-TR', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })
                                        : 'Tarih belirlenmemiş';
                                    const start = item.startTime ? item.startTime.substring(0, 5) : '';
                                    const end = item.endTime ? item.endTime.substring(0, 5) : '';
                                    return (
                                        <div key={key} className="border rounded p-3 mb-3">
                                            <div className="d-flex justify-content-between align-items-start flex-wrap">
                                                <div>
                                                    <div className="fw-semibold">{formattedDate}</div>
                                                    <div className="text-muted">
                                                        {start && end ? `${start} - ${end}` : 'Saat bilgisi bulunmuyor'}
                                                    </div>
                                                    <div className="mt-2">
                                                        <strong>{item.classroomCode}</strong>
                                                        {item.classroomName ? ` • ${item.classroomName}` : ''}
                                                        {item.buildingName ? ` • ${item.buildingName}` : ''}
                                                    </div>
                                                    {Array.isArray(item.exams) && item.exams.length > 0 && (
                                                        <ul className="mt-2 mb-0 pl-3">
                                                            {item.exams.map(exam => (
                                                                <li key={exam.examId}>
                                                                    {exam.courseCode ? `${exam.courseCode} - ` : ''}
                                                                    {exam.examName}
                                                                    {exam.courseName ? ` (${exam.courseName})` : ''}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    <div className="small text-muted mt-2">
                                                        Sınav sayısı: {item.examCount} • Oturma ataması: {item.seatAssignmentCount} • Görevli sayısı: {item.teachingAssignmentCount}
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-wrap align-self-start" style={{ gap: '0.5rem' }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => openSplitModal(item)}
                                                        disabled={splittingKey === key}
                                                        title={item.teachingAssignmentCount > 0 ? 'Görevliler yeni gruplara kopyalanır' : 'Sınavı gruplara böl'}
                                                    >
                                                        <i className="bi bi-diagram-3 me-1" aria-hidden="true"></i>
                                                        {splittingKey === key ? 'Bölünüyor...' : 'Sınavı Gruplara Böl'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleCancelOrganization(item)}
                                                        disabled={cancelingKey === key}
                                                    >
                                                        {cancelingKey === key ? 'İptal ediliyor...' : 'Organizasyonu İptal Et'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {status && <div className="alert alert-info mt-3" style={{ whiteSpace: 'pre-line' }}>{status}</div>}
                            </div>
                        )}
                        {drafts.length > 0 && (
                            <div className="mt-4">
                                <h6>Taslaklar</h6>
                                <div className="list-group">
                                    {drafts.map(draft => (
                                        <div key={draft.id} className="list-group-item d-flex justify-content-between align-items-center">
                                            <div>
                                                <div className="fw-semibold">{draft.label}</div>
                                                <small className="text-muted">{formatDraftDate(draft)}</small>
                                            </div>
                                            <div className="btn-group">
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleLoadDraft(draft)}
                                                >
                                                    Yükle
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteDraft(draft.id)}
                                                >
                                                    Sil
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                    <>
                        <h5>2. Slot Seç</h5>
                        <div className="mb-2">
                            <button
                                className="btn m-1"
                                style={{
                                    border: '1px solid #007bff',
                                    backgroundColor: selectedTimeSlots.length === timeSlots.length ? '#007bff' : 'transparent',
                                    color: selectedTimeSlots.length === timeSlots.length ? '#fff' : '#007bff'
                                }}
                                onClick={() => setSelectedTimeSlots(
                                    selectedTimeSlots.length === timeSlots.length
                                        ? []
                                        : timeSlots.map(ts => ts.id)
                                )}
                            >
                                {selectedTimeSlots.length === timeSlots.length ? 'Temizle' : 'Hepsini Seç'}
                            </button>
                        </div>
                        {Array.from(new Set(timeSlots.map(ts => ts.exactDate))).sort().map(date => {
                            const ids = timeSlots.filter(ts => ts.exactDate === date).map(ts => ts.id);
                            const allSel = ids.every(id => selectedTimeSlots.includes(id));
                            return (
                                <div key={date} className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6>{new Date(date).toLocaleDateString('tr-TR', {
                                            weekday: 'long', year: 'numeric', month: 'numeric', day: 'numeric'
                                        })}</h6>
                                        <button
                                            className="btn m-1"
                                            style={{
                                                border: '1px solid #007bff',
                                                backgroundColor: allSel ? '#007bff' : 'transparent',
                                                color: allSel ? '#fff' : '#007bff'
                                            }}
                                            onClick={() => setSelectedTimeSlots(prev =>
                                                allSel
                                                    ? prev.filter(x => !ids.includes(x))
                                                    : [...new Set([...prev, ...ids])]
                                            )}
                                        >
                                            {allSel ? 'Kaldır' : 'Seç'}
                                        </button>
                                    </div>
                                    <div className="d-flex flex-wrap">
                                        {ids.map(id => {
                                            const ts = timeSlots.find(x => x.id === id);
                                            const sel = selectedTimeSlots.includes(id);
                                            return (
                                                <button
                                                    key={id}
                                                    className="btn m-1"
                                                    style={{
                                                        border: '1px solid #007bff',
                                                        backgroundColor: sel ? '#007bff' : 'transparent',
                                                        color: sel ? '#fff' : '#007bff'
                                                    }}
                                                    onClick={() => setSelectedTimeSlots(prev =>
                                                        sel
                                                            ? prev.filter(x => x !== id)
                                                            : [...prev, id]
                                                    )}
                                                >
                                                    {`${ts.startTime.substring(0, 5)} - ${ts.endTime.substring(0, 5)}`}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                        <p>Seçilen Slot: {selectedTimeSlots.length}</p>
                    </>
                )}

                {/* STEP 3: Fakülte & Ders ve Gruplama */}
                {step === 3 && (
                    <>
                        <h5>3. Fakülte & Ders Seç</h5>
                        <select
                            className="form-control mb-3"
                            value={selectedFaculty || ''}
                            onChange={e => setSelectedFaculty(Number(e.target.value))}
                        >
                            <option value="" disabled>Fakülte Seçiniz</option>
                            {faculties.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>

                        {/* Exam selection */}
                        <div className="row">
                            <div className="col-md-6">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">Tüm Sınavlar</h6>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={async () => {
                                            const selectable = exams.filter(ex => !ex.alreadyScheduled);

                                            for (const exam of selectable) {
                                                await toggleExamSelection(exam);
                                            }
                                        }}
                                    >Tümünü Seç</button>
                                </div>
                                {exams.map(ex => {
                                    const disabled = ex.alreadyScheduled;
                                    return (
                                        <div className="form-check" key={ex.id}>
                                            <input
                                                type="checkbox"
                                                disabled={disabled}                    // ← seçimi engelle
                                                checked={selectedExams.includes(ex.id)}
                                                onChange={() => toggleExamSelection(ex)}
                                                className="form-check-input"
                                            />

                                            {/* metni sönükle & rozet ekle */}
                                            <label
                                                className={
                                                    'form-check-label ml-2' +
                                                    (disabled ? ' text-muted font-italic' : '')
                                                }
                                            >
                                                {ex.name} ({ex.studentCount} Kişi)
                                                {disabled && (
                                                    <span className="badge badge-secondary ml-2">
                                                        Planlandı
                                                    </span>
                                                )}
                                            </label>

                                            {ex.details && (
                                                <small className="text-muted d-block ml-4">{ex.details}</small>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="col-md-6">
                                <h6>Seçilen Sınavlar</h6>
                                {Object.entries(examsByFaculty).map(([fac, list]) => (
                                    <div key={fac} className="mb-3">
                                        <strong>{fac}</strong>
                                        <ul className="list-group list-group-flush">
                                            {list.map(ex => <li className="list-group-item" key={ex.id}>{ex.name} ({ex.studentCount} Kişi)</li>)}
                                        </ul>
                                    </div>
                                ))}
                                {/* ▶ Manuel Gruplama */}
                                <h6 className="mt-4">Manuel Sınav Gruplama</h6>
                                <small>Gruplandırmak istediğiniz sınavları seçip “Grupları Oluştur”</small>

                                {/* Ungrouped list */}
                                {ungroupedExams.length === 0 ? (
                                    <p className="text-muted">Gruplandırılacak sınav yok.</p>
                                ) : (
                                    ungroupedExams.map(id => {
                                        const ex = allExams.find(e => e.id === id);
                                        return (
                                            <div className="form-check" key={id}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedForGroup.includes(id)}
                                                    onChange={() => setSelectedForGroup(prev =>
                                                        prev.includes(id)
                                                            ? prev.filter(x => x !== id)
                                                            : [...prev, id]
                                                    )}
                                                />
                                                <label className="form-check-label ml-2">{ex?.name} ({ex.studentCount} Kişi)</label>
                                            </div>
                                        );
                                    })
                                )}
                                <button
                                    className="btn btn-sm btn-outline-success mt-2"
                                    disabled={selectedForGroup.length < 2}
                                    onClick={createGroup}
                                >
                                    {`Grupları Oluştur (${selectedStudentTotal} kişi)`}
                                </button>

                                {/* Manuel Gruplar */}
                                {examGroups.map((group, idx) => (
                                    <div key={idx} className="border p-2 my-2">
                                        <strong>Grup {idx + 1}:</strong>{' '}
                                        {group.map(id => allExams.find(e => e.id === id)?.name).join(', ')}
                                        <div className="small text-muted">Toplam {calculateGroupStudentCount(group)} öğrenci</div>
                                        <button
                                            className="btn btn-sm btn-outline-danger ml-2"
                                            onClick={() => removeGroup(idx)}
                                        >
                                            Sil
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {/* Step 4: Building & Rooms */}
                {step === 4 && (
                    <>
                        <h5>4. Bina & Sınıf Seç</h5>
                        <select className="form-control mb-3" value={selectedBuilding || ''} onChange={e => setSelectedBuilding(Number(e.target.value))}>
                            <option value="" disabled>Bina Seçiniz</option>
                            {buildings.map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}
                        </select>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">Tüm Odalar</h6>
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => {
                                        const ids = rooms.map(r => r.id);
                                        setSelectedRooms(prev => Array.from(new Set([...prev, ...ids])));
                                    }}>Tümünü Seç</button>
                                </div>
                                {rooms.map(r => (
                                    <div className="form-check" key={r.id}>
                                        <input className="form-check-input" type="checkbox" checked={selectedRooms.includes(r.id)} onChange={() => {
                                            setSelectedRooms(prev => prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id]);
                                        }} />
                                        <label className="form-check-label ml-2">{r.name}</label>
                                    </div>
                                ))}
                            </div>
                            <div className="col-md-6">
                                <h6>Seçilen Odalar</h6>
                                {Object.entries(roomsByBuilding).length === 0 ? (
                                    <p className="text-muted">Henüz oda seçilmedi.</p>
                                ) : (
                                    Object.entries(roomsByBuilding).map(([bld, list]) => (
                                        <div key={bld} className="mb-3">
                                            <strong>{bld}</strong>
                                            <ul className="list-group list-group-flush">
                                                {list.map(r => (<li className="list-group-item" key={r.id}>{r.name}</li>))}
                                            </ul>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* Step 5: Confirmation */}
                {step === 5 && (
                    <>
                        <h5>5. Onay & Planla</h5>
                        <p><strong>Dönem:</strong> {findPeriodName(selectedExamPeriod)}</p>
                        <p><strong>Kriterler:</strong> Aynı gün en fazla {maxDailyExamCount} sınav, kendi bölümünden en fazla {maxDailyOwnDepartmentExamCount} sınav</p>

                        <h6>Slotlar</h6>
                        <div>
                            {Object.entries(formatSlots()).map(([date, times]) => (
                                <div key={date} className="mb-2">
                                    <strong>{date}:</strong> {times.join(', ')}
                                </div>
                            ))}
                        </div>

                        {/* ▶ Manuel Gruplar Özeti */}
                        {examGroups.length > 0 && (
                            <div className="mt-4">
                                <h6>Manuel Gruplar</h6>
                                {examGroups.map((group, i) => (
                                    <div key={i} className="mb-3">
                                        <strong>Grup {i + 1}:</strong> {group.length} sınav • Toplam {calculateGroupStudentCount(group)} öğrenci
                                        <ul className="mb-0">
                                            {group.map(examId => {
                                                const ex = allExams.find(e => e.id === examId);
                                                return <li key={examId}>{ex?.name}</li>;
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}

                        <h6 className="mt-4">Tekli Sınavlar</h6>
                        <div>
                            {/* Gruplara girmeyen sınavlar */}
                            {ungroupedExams.map(id => {
                                const ex = allExams.find(e => e.id === id);
                                return <div key={id}>{ex?.name}</div>;
                            })}
                        </div>

                        <h6 className="mt-4">Odalar</h6>
                        <div>
                            {Object.entries(roomsByBuilding).map(([bld, list]) => (
                                <div key={bld} className="mb-2">
                                    <strong>{bld}</strong>
                                    <ul className="mb-0">
                                        {list.map(r => <li key={r.id}>{r.name}</li>)}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        <div className="d-flex flex-wrap mt-4" style={{ gap: '0.5rem' }}>
                            <button className="btn btn-outline-secondary" onClick={handleSaveDraft}>Taslak Kaydet</button>
                            <button className="btn btn-primary" onClick={handleSubmit}>Planla</button>
                        </div>
                        {status && <div className="alert alert-info mt-3" style={{ whiteSpace: 'pre-line' }}>{status}</div>}
                    </>
                )}


                {splitTarget && (
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)' }}>
                        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <div>
                                        <h5 className="modal-title">Sınavı Gruplara Böl</h5>
                                        <div className="text-muted small">
                                            {formatSlotLabel(splitSourceSlot)} • {splitTarget.classroomCode} • {splitExpectedTotal} öğrenci
                                        </div>
                                    </div>
                                    <button type="button" className="btn-close" aria-label="Kapat" onClick={closeSplitModal}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row g-3 align-items-end mb-4">
                                        <div className="col-md-3">
                                            <label className="form-label">Kaça bölünecek?</label>
                                            <input
                                                type="number"
                                                min="2"
                                                max="12"
                                                className="form-control"
                                                value={splitGroupCount}
                                                onChange={e => handleSplitCountChange(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-9">
                                            <div className={`alert mb-0 py-2 ${splitActualTotal === splitExpectedTotal ? 'alert-light border' : 'alert-warning'}`}>
                                                Dağıtılan öğrenci: {splitActualTotal} / {splitExpectedTotal}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table align-middle">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 90 }}>Grup</th>
                                                    <th style={{ minWidth: 220 }}>Slot</th>
                                                    <th style={{ minWidth: 180 }}>Bina</th>
                                                    <th style={{ minWidth: 180 }}>Sınıf</th>
                                                    <th style={{ width: 150 }}>Öğrenci</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {splitRows.map((row, index) => {
                                                    const roomOptions = splitRoomsByBuilding[row.buildingId] || [];
                                                    return (
                                                        <tr key={index}>
                                                            <td>
                                                                <span className="badge badge-light-primary">Grup {index + 1}</span>
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select"
                                                                    value={row.timeSlotId || ''}
                                                                    onChange={e => updateSplitRow(index, { timeSlotId: e.target.value ? Number(e.target.value) : '' })}
                                                                >
                                                                    <option value="">Slot seçiniz</option>
                                                                    {timeSlots.map(slot => (
                                                                        <option key={slot.id} value={slot.id}>{formatSlotLabel(slot)}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select"
                                                                    value={row.buildingId || ''}
                                                                    onChange={async e => {
                                                                        const buildingId = e.target.value ? Number(e.target.value) : '';
                                                                        updateSplitRow(index, { buildingId, classroomId: '' });
                                                                        if (buildingId) {
                                                                            await ensureSplitRoomsForBuilding(buildingId);
                                                                        }
                                                                    }}
                                                                >
                                                                    <option value="">Bina seçiniz</option>
                                                                    {splitBuildings.map(building => (
                                                                        <option key={building.id} value={building.id}>{building.name}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select"
                                                                    value={row.classroomId || ''}
                                                                    onChange={e => updateSplitRow(index, { classroomId: e.target.value ? Number(e.target.value) : '' })}
                                                                    disabled={!row.buildingId}
                                                                >
                                                                    <option value="">Sınıf seçiniz</option>
                                                                    {roomOptions.map(room => (
                                                                        <option key={room.id} value={room.id}>{room.name}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    className="form-control"
                                                                    value={row.studentCount}
                                                                    onChange={e => updateSplitRow(index, { studentCount: Number(e.target.value) || 0 })}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light" onClick={closeSplitModal}>Vazgeç</button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSplitSubmit}
                                        disabled={splittingKey === `${splitTarget.timeSlotId}_${splitTarget.classroomId}`}
                                    >
                                        {splittingKey === `${splitTarget.timeSlotId}_${splitTarget.classroomId}` ? 'Bölünüyor...' : 'Böl ve Kaydet'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Nav Buttons */}
                <div className="mt-4 d-flex justify-content-between">
                    <button className="btn btn-secondary" onClick={prev} disabled={step === 1}>Geri</button>
                    <button className="btn btn-secondary" onClick={next} disabled={step === 5}>{step < 5 ? 'İleri' : ''}</button>
                </div>
            </div>
        </div>
    );
}
